import type { KeyboardEvent, ReactNode } from 'react';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { SHELF_FOR_KEY, SHELF_SHORTCUT, type ShelfId } from './fields';

export interface FieldMenuAction {
  readonly label: string;
  /** The key shown and accepted: `Alt+↑`, `Del`. Display only. */
  readonly shortcut?: string;
  readonly onClick: () => void;
  readonly disabled?: boolean;
}

export interface FieldMenuProps {
  readonly anchorEl: HTMLElement | null;
  readonly open: boolean;
  readonly onClose: () => void;
  /** The accessible name of the menu: "Aktionen für Police Status". */
  readonly label: string;
  /** "Verschieben nach …" targets; the current shelf is left out by the caller. */
  readonly moveTargets: readonly ShelfId[];
  readonly onMove: (shelf: ShelfId) => void;
  /** Within the shelf (up, down, remove) and display (title, format) — mockup board 6. */
  readonly within?: readonly FieldMenuAction[];
  readonly display?: readonly FieldMenuAction[];
}

/**
 * The `⋮` menu of a field or a shelf token (SDD-024 U-2; mockup board 6): "Verschieben nach
 * Zeilen R / Spalten C / Werten V / Filter F", the moves within the shelf and the display
 * actions. The shortcut letters work while the menu is open, so the keyboard path is "focus the
 * field, Enter, the letter" — the path one BDD scenario uses exclusively.
 */
export function FieldMenu({
  anchorEl,
  open,
  onClose,
  label,
  moveTargets,
  onMove,
  within = [],
  display = [],
}: FieldMenuProps): ReactNode {
  const { t } = useTranslation('patterns');
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const shelf = SHELF_FOR_KEY[event.key.toLowerCase()];
    if (shelf !== undefined && moveTargets.includes(shelf)) {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      onMove(shelf);
    }
  };
  const action = (a: FieldMenuAction): ReactNode => (
    <MenuItem
      key={a.label}
      dense
      disabled={a.disabled === true}
      onClick={() => {
        onClose();
        a.onClick();
      }}
    >
      <ListItemText>{a.label}</ListItemText>
      {a.shortcut !== undefined && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }} aria-hidden>
          {a.shortcut}
        </Typography>
      )}
    </MenuItem>
  );
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      onKeyDown={onKeyDown}
      slotProps={{ list: { 'aria-label': label, dense: true } }}
    >
      {moveTargets.map((shelf) => (
        <MenuItem
          key={shelf}
          dense
          onClick={() => {
            onClose();
            onMove(shelf);
          }}
        >
          <ListItemText>{t(`reporting.fields.moveTo.${shelf}`)}</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }} aria-hidden>
            {SHELF_SHORTCUT[shelf]}
          </Typography>
        </MenuItem>
      ))}
      {within.length > 0 && <Divider />}
      {within.length > 0 && (
        <ListSubheader sx={{ lineHeight: 2 }}>{t('reporting.shelf.within')}</ListSubheader>
      )}
      {within.map(action)}
      {display.length > 0 && <Divider />}
      {display.length > 0 && (
        <ListSubheader sx={{ lineHeight: 2 }}>{t('reporting.shelf.display')}</ListSubheader>
      )}
      {display.map(action)}
    </Menu>
  );
}
